import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Home as HomeIcon, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { FormError, FormBanner } from '../components/FormError';
import useFormValidation from '../hooks/useFormValidation';
import SEO from '../components/SEO';
import GoogleButton from '../components/GoogleButton';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const v = useFormValidation();

  const from = location.state?.from || '/mi-cuenta';

  const set = (k, val) => { setForm((f) => ({ ...f, [k]: val })); v.clearField(k); };

  const submit = async (e) => {
    e.preventDefault();
    v.reset();
    const ok = v.validate({
      email: !form.email ? 'Email es obligatorio.'
        : !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email) ? 'Email no tiene un formato válido.'
        : null,
      password: !form.password ? 'Contraseña es obligatoria.' : null,
    });
    if (!ok) return;
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate(from, { replace: true });
    } catch (err) {
      v.applyError(err, 'No pudimos iniciar sesión con esas credenciales.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-habita-cream grid lg:grid-cols-2">
      <SEO title="Iniciar sesión" description="Accede a tu cuenta de habita.co para gestionar propiedades, favoritos y leads." noindex />
      <div className="flex flex-col p-8 lg:p-14">
        <Link to="/" className="flex items-center gap-2 mb-12">
          <div className="w-9 h-9 rounded-md bg-habita flex items-center justify-center"><HomeIcon className="w-5 h-5 text-white" strokeWidth={2.5} /></div>
          <span className="font-serif font-bold text-2xl text-habita">habita<span className="text-amber-500">.</span><span className="text-gray-800">co</span></span>
        </Link>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md">
            <h1 className="font-serif text-4xl lg:text-5xl font-bold text-habita-dark">Bienvenido de vuelta</h1>
            <p className="text-gray-600 mt-3">Inicia sesión para administrar tus propiedades, ver mensajes y mucho más.</p>

            <form onSubmit={submit} className="mt-8 space-y-4" noValidate>
              <FormBanner msg={v.banner} testid="login-error-banner" />

              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Email</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="email"
                    value={form.email}
                    onChange={e => set('email', e.target.value)}
                    className={`h-12 pl-10 ${v.errorClass('email')}`}
                    placeholder="tu@correo.com"
                    data-testid="login-email-input"
                    {...v.field('email')}
                  />
                </div>
                <FormError msg={v.fieldErrors.email} testid="login-error-email" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Contraseña</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <Input
                    type={showPwd ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => set('password', e.target.value)}
                    className={`h-12 pl-10 pr-12 ${v.errorClass('password')}`}
                    placeholder="••••••••"
                    data-testid="login-password-input"
                    autoComplete="current-password"
                    {...v.field('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(!showPwd)}
                    className="absolute right-1 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full text-gray-500 hover:text-habita hover:bg-habita-mint transition-colors z-10"
                    aria-label={showPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    aria-pressed={showPwd}
                    tabIndex={-1}
                    data-testid="login-toggle-password"
                  >
                    {showPwd ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
                  </button>
                </div>
                <FormError msg={v.fieldErrors.password} testid="login-error-password" />
              </div>
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 accent-[#00824D]" />
                  <span className="text-gray-600">Recordarme</span>
                </label>
                <a href="/recuperar" className="text-habita font-semibold hover:underline">¿Olvidaste tu contraseña?</a>
              </div>
              <Button type="submit" disabled={loading} className="btn-habita w-full h-12 rounded-full font-semibold disabled:opacity-60" data-testid="login-submit-btn">
                {loading ? 'Ingresando...' : 'Iniciar sesión'}
              </Button>
            </form>

            <div className="flex items-center gap-3 my-6">
              <div className="h-px bg-gray-200 flex-1" />
              <span className="text-xs uppercase tracking-widest text-gray-400">o</span>
              <div className="h-px bg-gray-200 flex-1" />
            </div>
            <GoogleButton />

            <p className="text-sm text-gray-600 mt-8 text-center">
              ¿No tienes cuenta? <Link to="/registro" state={{ from }} className="font-semibold text-habita hover:underline">Regístrate gratis</Link>
            </p>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative bg-habita-dark">
        <img src="https://images.unsplash.com/photo-1505843513577-22bb7d21e455?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob3VzZXxlbnwwfHx8fDE3Nzk0MjAyNTh8MA&ixlib=rb-4.1.0&q=85" alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-tr from-habita-dark/90 via-habita-dark/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <span className="text-xs uppercase tracking-widest text-amber-300 font-semibold">habita.co</span>
          <h2 className="font-serif text-4xl lg:text-5xl font-bold mt-3 leading-tight">Encuentra el hogar que mereces.</h2>
          <p className="text-white/80 mt-4 max-w-md">Más de 25.000 propiedades en las mejores zonas de Colombia.</p>
        </div>
      </div>
    </div>
  );
}
